import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  VaRadio,
  VaRadioOption,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';

import SubTaskContainerHeader from '../SubTaskContainerHeader';
import { usePathHistory } from '../utils/tracking/PathHistoryContext';
import {
  trackScreenerStepView,
  trackPactEligibilitySelection,
  trackScreenerValidationError,
} from '../../utils/tracking/features/screenerTracking';
import pageNames from './pageNames';

const dataKey = 'view:pactEligibility';
const militaryStatusKey = 'view:militaryStatus';

const content = {
  label:
    'Were you exposed to hazardous chemicals or materials during your service?',
  errorMessage: 'Select an option that applies to your exposure history',
};

const options = [
  {
    value: 'yes',
    label: 'Yes',
    description:
      'I served in Vietnam, the Gulf War, took part in post-9/11 operations, or was exposed to hazardous chemicals or materials during service.',
  },
  { value: 'no', label: 'No, I don’t believe this applies to me' },
  { value: 'not-sure', label: 'I’m not sure' },
];

const optionValues = options.map(option => option.value);

const validate = data => optionValues.includes(data[dataKey]);

/**
 * Start 2b — PACT Act eligibility screener
 * @param {Object} data - subtask data
 * @param {Boolean} error - page submitted & error state
 * @param {Function} setPageData - updates subtask data
 * @returns {JSX}
 */
const PactEligibilityPage = ({ data = {}, error, setPageData }) => {
  const { addStep, getHistory } = usePathHistory();

  useEffect(() => {
    trackScreenerStepView({
      stepName: pageNames.pactEligibility,
      pathHistoryContext: { addStep, getHistory },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) {
      trackScreenerValidationError({
        pageName: pageNames.pactEligibility,
        fieldName: 'pact-eligibility',
        errorMessage: content.errorMessage,
      });
    }
  }, [error]);

  const handleSelect = ({ target }) => {
    setPageData({ [dataKey]: target.value || null });
  };

  return (
    <>
      <SubTaskContainerHeader />
      <VaRadio
        label={content.label}
        labelHeaderLevel="2"
        error={error ? content.errorMessage : null}
        onRadioOptionSelected={handleSelect}
        className="vads-u-margin-top--0"
        required
      >
        {options.map(({ value, label, description }) => (
          <VaRadioOption
            key={value}
            className="vads-u-margin-y--2"
            name="pactEligibility"
            id={value}
            value={value}
            label={label}
            description={description}
            checked={value === data[dataKey]}
            tile
          />
        ))}
      </VaRadio>
      <va-details
        label="Why we ask this question"
        class="vads-u-display--block vads-u-margin-top--3"
      >
        <p className="vads-u-margin-top--0">
          Your response will let us know if you may be eligible for disability
          benefits under the PACT Act.
        </p>
      </va-details>
    </>
  );
};

PactEligibilityPage.propTypes = {
  data: PropTypes.shape({}),
  error: PropTypes.bool,
  setPageData: PropTypes.func,
};

export default {
  name: pageNames.pactEligibility,
  component: PactEligibilityPage,
  validate,
  back: pageNames.claimType,
  next: pageNames.evidenceNeeded,
  onContinue: data =>
    trackPactEligibilitySelection({
      pactEligibility: data[dataKey],
      isActiveDuty: data[militaryStatusKey] === 'active-duty',
    }),
};
